
import React from 'react';
import { format } from 'date-fns';
import { X, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import CalendarCampaignPill from './CalendarCampaignPill';
import CalendarEventDot from './CalendarEventDot';

interface Campaign {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
  platform_targeted: string[] | null;
  brands?: {
    name: string;
  } | null;
}

interface Event {
  id: string;
  name: string;
  event_date: string;
  event_time: string | null;
  event_type: string;
  venue_address: string | null;
  campaigns?: {
    name: string;
  } | null;
}

interface CalendarDayModalProps {
  date: Date;
  campaigns: Campaign[];
  events: Event[];
  showCampaigns: boolean;
  showEvents: boolean;
  onClose: () => void;
}

const CalendarDayModal: React.FC<CalendarDayModalProps> = ({
  date,
  campaigns,
  events,
  showCampaigns,
  showEvents,
  onClose,
}) => {
  const dayStr = format(date, 'yyyy-MM-dd');

  const dayEvents = events.filter(event => {
    if (!event.event_date) return false;
    return event.event_date.substring(0, 10) === dayStr;
  });

  const dayCampaigns = campaigns.filter(campaign => {
    if (!campaign.start_date || !campaign.end_date) return false;
    const startStr = campaign.start_date.substring(0, 10);
    const endStr = campaign.end_date.substring(0, 10);
    return dayStr >= startStr && dayStr <= endStr;
  });

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calendar className="w-5 h-5 text-primary" />
            {format(date, 'EEEE, MMMM d, yyyy')}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto space-y-6">
          {showCampaigns && dayCampaigns.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                Active Campaigns ({dayCampaigns.length})
              </h3>
              <div className="space-y-3">
                {dayCampaigns.map((campaign) => (
                  <CalendarCampaignPill key={campaign.id} campaign={campaign} expanded />
                ))}
              </div>
            </div>
          )}
          
          {showEvents && dayEvents.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                Events ({dayEvents.length})
              </h3>
              <div className="space-y-3">
                {dayEvents.map((event) => (
                  <CalendarEventDot key={event.id} event={event} expanded showTime />
                ))}
              </div>
            </div>
          )}

          {(!showCampaigns || dayCampaigns.length === 0) && (!showEvents || dayEvents.length === 0) && (
            <div className="text-center text-muted-foreground py-12">
              <div className="text-lg font-medium mb-2">No items scheduled</div>
              <div className="text-sm">This day is free of campaigns and events.</div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarDayModal;
